import type { SearchFilterApi } from '@ingenico/api-sdk-configuration-public';
import type { DeepOmitReadonly } from '@ingenico/api-sdk-core';
import type {
  CreateDeploymentRequestV2MutationRequestApi,
  DeploymentRequestResourceDynamicGroupV2Api,
  DeploymentRequestResourceOptionDeleteV2Api,
  DeploymentRequestResourceReadV2Api,
  DeploymentRequestResourceTriggerOptionV2Api,
  DeploymentRequestResourceTriggerV2Api,
  SearchRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumDeploymentRequestTriggerV2 } from '@ingenico/api-sdk-device-management-private-v2';
import { isEmpty, toArray, toString } from 'lodash-es';
import moment from 'moment';
import type { AdvancedSchedulingValues } from '../../../../components/advanced-scheduling-section/AdvancedSchedulingSection.types';
import type { SearchFilter, SearchRequest } from '../../../models/common.model';
import {
  ScheduleActivitySeverity,
  type AppDeletionBody,
  type DeploymentRequestBody,
  type ScheduleOption,
  type SimpleDeploymentRequestResponse,
  type SystemPackageDeletionBody,
} from '../../../models/deployment/deploymentRequest.model';
import { enumReferenceMode } from '../../../models/deployment/enums.model';
import { normalizeDateTimeToUnit } from '../../../models/time/timeUnitNormalization.const';
import { mapAdvancedSchedulingToPayload } from './advancedScheduling.mapper';

type DeviceTimeDateValue = Date | string | undefined;

export interface DeviceTimeSlotPayload {
  readonly startDate: Date;
  readonly endDate?: Date;
}

function hasTimeZone(value: string): boolean {
  return /(Z|[+-]\d{2}:\d{2})$/u.test(value);
}

export function mapDeviceTimeValueToUtcDate(value: DeviceTimeDateValue): Date | undefined {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  if (hasTimeZone(value)) {
    const parsedDate = moment(value);
    return parsedDate.isValid() ? parsedDate.toDate() : undefined;
  }

  const parsedDate = moment.utc(value);
  return parsedDate.isValid() ? parsedDate.toDate() : undefined;
}

export function mapDeviceTimeSlotForRequest(timeSlot?: {
  readonly start?: DeviceTimeDateValue;
  readonly end?: DeviceTimeDateValue;
}): DeviceTimeSlotPayload | undefined {
  const startDate = normalizeDateTimeToUnit(mapDeviceTimeValueToUtcDate(timeSlot?.start), 'minute');

  if (!startDate) {
    return undefined;
  }

  const mappedEndDate = normalizeDateTimeToUnit(mapDeviceTimeValueToUtcDate(timeSlot?.end), 'minute');
  const endDate = mappedEndDate
    ? mappedEndDate.getTime() <= startDate.getTime()
      ? moment(mappedEndDate).add(1, 'day').toDate()
      : mappedEndDate
    : undefined;

  return {
    startDate,
    endDate,
  };
}

export function mapDeploymentRequestResourceReadV2ApiToSimpleDeploymentRequestResponse({
  id,
  name,
  status,
}: DeploymentRequestResourceReadV2Api): SimpleDeploymentRequestResponse {
  return {
    id,
    name,
    status,
  };
}

export function mapDeploymentRequestBody(
  data: DeploymentRequestBody,
  initialGroupsQuery: SearchRequest | undefined,
  additionalFiltersMapped?: SearchFilter[],
  isProgressiveDeploymentEnabled = false,
): CreateDeploymentRequestV2MutationRequestApi {
  return {
    name: toString(data.name),
    ...(data.description?.trim() ? { description: data.description } : {}),
    trigger: buildTriggerObject(data.scheduleOption, data.advancedScheduling, isProgressiveDeploymentEnabled),
    dynamicGroup: buildDynamicGroupForDeploymentRequest(
      initialGroupsQuery,
      additionalFiltersMapped,
      data.isCheckedAdvancedFilters,
      data.groupId,
    ),
    options: {
      configuration: {
        reference: {
          id: toString(data.configurationId),
        },
        type: enumReferenceMode.ID,
      },
    },
  };
}

export function mapDeployDeletionRequestBody(
  data: DeploymentRequestBody,
  additionalFiltersMapped?: SearchFilterApi[],
  isProgressiveDeploymentEnabled = false,
): CreateDeploymentRequestV2MutationRequestApi {
  return {
    name: toString(data.name),
    ...(data.description?.trim() ? { description: data.description } : {}),
    trigger: buildTriggerObject(data.scheduleOption, data.advancedScheduling, isProgressiveDeploymentEnabled),
    dynamicGroup: {
      query: {
        sort: {},
        filters: [...(additionalFiltersMapped ?? [])],
      },
      type: enumReferenceMode.CUSTOM,
    },
    options: {
      delete: buildDeleteForDeployDeletionRequest(data.applications, data.systemPackages),
    },
  };
}

export function buildDynamicGroupForDeploymentRequest(
  initialGroupsQuery?: SearchRequest,
  additionalFiltersMapped?: SearchFilter[],
  isCheckedAdvancedFilters?: boolean,
  groupId?: string,
): DeepOmitReadonly<DeploymentRequestResourceDynamicGroupV2Api> {
  return {
    query: buildQuery(initialGroupsQuery, additionalFiltersMapped, isCheckedAdvancedFilters),
    reference: isEmpty(additionalFiltersMapped) || !isCheckedAdvancedFilters ? { id: toString(groupId) } : undefined,
    type:
      isEmpty(additionalFiltersMapped) || !isCheckedAdvancedFilters ? enumReferenceMode.ID : enumReferenceMode.CUSTOM,
  };
}

export function buildDeleteForDeployDeletionRequest(
  applications: AppDeletionBody[] | undefined,
  systemPackages: SystemPackageDeletionBody[] | undefined,
): DeploymentRequestResourceOptionDeleteV2Api {
  return {
    filePaths: [],
    applicationVersionsIds:
      applications?.reduce((accum, app) => [...accum, ...app.applicationVersionIds], [] as string[]) ?? [],
    systemPackageVersionsIds:
      systemPackages?.reduce((accum, pack) => [...accum, ...pack.systemPackagesVersionIds], [] as string[]) ?? [],
    packageIds: [],
  };
}

export function buildQuery(
  initialGroupsQuery?: SearchRequest,
  additionalFiltersMapped?: SearchFilter[],
  isCheckedAdvancedFilters?: boolean,
): SearchRequestApi | undefined {
  return isEmpty(additionalFiltersMapped) || !isCheckedAdvancedFilters
    ? undefined
    : {
        sort: initialGroupsQuery?.sort ?? {},
        filters: [...(initialGroupsQuery?.filters ?? []), ...(additionalFiltersMapped ?? [])],
      };
}

export function buildDeploymentScheduleOptions(
  date?: Date,
  timeSlotPayload?: {
    startDate: Date;
    endDate?: Date;
  },
  exclusionDatesPayload?: Date[],
  maxNoOfDeploymentsPayload?: number,
  daysOfWeekPayload?: string[],
): DeploymentRequestResourceTriggerOptionV2Api {
  const triggerScheduleOption: DeploymentRequestResourceTriggerOptionV2Api = {
    ...(date ? { date } : {}),
    ...(isEmpty(timeSlotPayload) ? {} : { timeSlot: timeSlotPayload }),
    ...(isEmpty(exclusionDatesPayload) ? {} : { exclusionDates: exclusionDatesPayload }),
    ...(maxNoOfDeploymentsPayload !== undefined ? { maxNoOfDeployments: maxNoOfDeploymentsPayload } : {}),
    ...(isEmpty(daysOfWeekPayload)
      ? {}
      : { daysOfWeek: daysOfWeekPayload as DeploymentRequestResourceTriggerOptionV2Api['daysOfWeek'] }),
  };
  return triggerScheduleOption;
}

export function buildScheduledObject(scheduleOption: ScheduleOption): DeploymentRequestResourceTriggerV2Api {
  const { date, exclusionDates, timeSlot } = scheduleOption ?? {};
  const exclusionDatesPayload = toArray(
    exclusionDates?.filter((dates) => !!dates.date).map((exclusionDate) => exclusionDate.date ?? new Date()),
  );
  const timeSlotPayload = isEmpty(timeSlot) ? undefined : mapDeviceTimeSlotForRequest(timeSlot);

  const triggerScheduleOption: DeploymentRequestResourceTriggerOptionV2Api = buildDeploymentScheduleOptions(
    date,
    timeSlotPayload,
    exclusionDatesPayload,
  );

  return {
    ...(isEmpty(triggerScheduleOption)
      ? {}
      : {
          scheduleOption: triggerScheduleOption,
        }),
    type:
      scheduleOption.severity === ScheduleActivitySeverity.SelectDate
        ? enumDeploymentRequestTriggerV2.SCHEDULED
        : enumDeploymentRequestTriggerV2.AUTOMATIC,
  };
}

function buildProgressiveScheduledObject(
  scheduleOption: ScheduleOption,
  advancedScheduling?: AdvancedSchedulingValues,
): DeploymentRequestResourceTriggerV2Api {
  const overlay = mapAdvancedSchedulingToPayload(advancedScheduling);
  const timeSlot = overlay.timeSlot ? mapDeviceTimeSlotForRequest(advancedScheduling?.timeSlot) : undefined;

  const triggerScheduleOption = buildDeploymentScheduleOptions(
    scheduleOption.date,
    timeSlot,
    overlay.exclusionDates,
    overlay.deploymentsPerDay,
    overlay.exclusionDays as DeploymentRequestResourceTriggerOptionV2Api['daysOfWeek'],
  );

  return {
    scheduleOption: triggerScheduleOption,
    type:
      scheduleOption.severity === ScheduleActivitySeverity.SelectDate
        ? enumDeploymentRequestTriggerV2.SCHEDULED
        : enumDeploymentRequestTriggerV2.AUTOMATIC,
  };
}

export function buildTriggerObject(
  scheduleOption: ScheduleOption | undefined,
  advancedScheduling?: AdvancedSchedulingValues,
  isProgressiveDeploymentEnabled = false,
): DeploymentRequestResourceTriggerV2Api {
  if (!scheduleOption) {
    return {
      type: enumDeploymentRequestTriggerV2.AUTOMATIC,
    };
  }
  if (isProgressiveDeploymentEnabled) {
    return buildProgressiveScheduledObject(scheduleOption, advancedScheduling);
  }
  return buildScheduledObject(scheduleOption);
}
